import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { GALLERY_CATEGORIES } from '../data/galeria.js';
import { useModelsForGallery, useShortFilmsForGallery } from '../context/GalleryConfigContext.jsx';
import '../styles/GaleriaPage.css';

function GaleriaPage() {
  const models = useModelsForGallery();
  const shortFilms = useShortFilmsForGallery();
  const [category, setCategory] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const [videoModal, setVideoModal] = useState(null);

  const filteredModels =
    category === 'all'
      ? [...models]
      : models.filter((m) => m.category === category);

  const openLightbox = (model) => {
    setLightbox(model);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightbox(null);
    document.body.style.overflow = '';
  };

  const openVideo = (film) => setVideoModal(film);
  const closeVideo = () => setVideoModal(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeLightbox();
        closeVideo();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="App">
      <Header />
      <main className="tm-galeria-page">
        {/* Hero */}
        <section className="tm-galeria-hero">
          <div className="tm-container">
            <div className="tm-galeria-hero-inner">
              <div className="tm-galeria-badge">
                <span className="material-symbols-outlined tm-galeria-badge-icon">star</span>
                <span>The Elite Portfolio</span>
              </div>
              <h1 className="tm-galeria-title">Galería de Modelos</h1>
              <div className="tm-galeria-divider" />
              <p className="tm-galeria-subtitle">
                Descubre el talento profesional que redefine la estética digital. Sesiones exclusivas en nuestros estudios lifestyle de primer nivel.
              </p>
            </div>
          </div>
        </section>

        {/* Filtros */}
        <section className="tm-galeria-filters">
          <div className="tm-container">
            <div className="tm-galeria-filters-inner">
              <div className="tm-galeria-categories">
                {GALLERY_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`tm-galeria-category-btn ${category === cat.id ? 'active' : ''}`}
                    onClick={() => setCategory(cat.id)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <div className="tm-galeria-sort">
                <span className="tm-galeria-sort-label">Ordenar por</span>
                <div className="tm-galeria-sort-value">
                  <span>Reciente</span>
                  <span className="material-symbols-outlined tm-galeria-sort-icon">expand_more</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Grid de modelos */}
        <section className="tm-galeria-grid-section">
          <div className="tm-container">
            <div className="tm-galeria-grid">
              {filteredModels.map((model) => (
                <button
                  key={model.id}
                  type="button"
                  className="tm-galeria-model-card"
                  onClick={() => openLightbox(model)}
                  aria-label={`Ver ${model.name} en grande`}
                >
                  <div className="tm-galeria-model-img-wrap">
                    <img src={model.src} alt={model.name} className="tm-galeria-model-img" loading="lazy" />
                  </div>
                  <div className="tm-galeria-model-overlay">
                    <span className="tm-galeria-model-tag">{model.tag}</span>
                    <p className="tm-galeria-model-location">{model.location}</p>
                  </div>
                  <span className="tm-galeria-model-fav material-symbols-outlined" aria-hidden>favorite</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Cortometrajes */}
        {shortFilms.length > 0 && (
        <section className="tm-galeria-shortfilms">
          <div className="tm-container">
            <div className="tm-galeria-shortfilms-header">
              <div>
                <h2 className="tm-galeria-shortfilms-title">Cortometrajes</h2>
                <p className="tm-galeria-shortfilms-subtitle">Lifestyle & Studio Sessions Showcase</p>
              </div>
              <button type="button" className="tm-galeria-shortfilms-link" onClick={() => document.getElementById('shortfilms-grid')?.scrollIntoView({ behavior: 'smooth' })}>
                <span>Explorar Cinema</span>
                <span className="material-symbols-outlined">arrow_right_alt</span>
              </button>
            </div>
            <div id="shortfilms-grid" className="tm-galeria-shortfilms-grid">
              {shortFilms.map((film) => (
                <button
                  key={film.id}
                  type="button"
                  className="tm-galeria-video-card"
                  onClick={() => openVideo(film)}
                  aria-label={`Reproducir ${film.title}`}
                >
                  <div className="tm-galeria-video-thumb" style={{ backgroundImage: `url(${film.thumbnail})` }} />
                  <div className="tm-galeria-video-overlay">
                    <div className="tm-galeria-video-play">
                      <span className="material-symbols-outlined">play_arrow</span>
                    </div>
                  </div>
                  <div className="tm-galeria-video-info">
                    <h3 className="tm-galeria-video-title">{film.title}</h3>
                    <p className="tm-galeria-video-subtitle">{film.subtitle}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
        )}

        {/* CTA */}
        <section className="tm-galeria-cta">
          <div className="tm-galeria-cta-bg" />
          <div className="tm-container">
            <div className="tm-galeria-cta-card">
              <h2 className="tm-galeria-cta-title">¿Quieres ser el próximo rostro de TM Studio?</h2>
              <p className="tm-galeria-cta-desc">
                Ofrecemos las mejores instalaciones lifestyle, soporte técnico 24/7 y un equipo de marketing dedicado para llevar tu carrera al siguiente nivel.
              </p>
              <div className="tm-galeria-cta-buttons">
                <Link to="/registro" className="btn-hover gold-gradient-bg tm-galeria-cta-btn">
                  Únete al equipo
                  <span className="material-symbols-outlined">groups</span>
                </Link>
                <Link to="/#beneficios" className="tm-galeria-cta-btn-outline">
                  Ver beneficios
                  <span className="material-symbols-outlined">info</span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Lightbox modelo */}
      {lightbox && (
        <div
          className="tm-models-lightbox-overlay"
          onClick={closeLightbox}
          role="presentation"
        >
          <div
            className="tm-models-lightbox"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Imagen de ${lightbox.name}`}
          >
            <button type="button" className="tm-models-lightbox-close" onClick={closeLightbox} aria-label="Cerrar">
              <span className="material-symbols-outlined">close</span>
            </button>
            <div className="tm-models-lightbox-image-wrap">
              <img src={lightbox.src} alt={lightbox.name} className="tm-models-lightbox-image" />
            </div>
            <div className="tm-models-lightbox-info">
              <p className="tm-models-lightbox-tag">{lightbox.tag}</p>
              {lightbox.location && <p className="tm-galeria-lightbox-location">{lightbox.location}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Modal video (placeholder: sin URL real solo overlay) */}
      {videoModal && (
        <div
          className="tm-models-lightbox-overlay"
          onClick={closeVideo}
          role="presentation"
        >
          <div
            className="tm-models-lightbox tm-galeria-video-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Video ${videoModal.title}`}
          >
            <button type="button" className="tm-models-lightbox-close" onClick={closeVideo} aria-label="Cerrar">
              <span className="material-symbols-outlined">close</span>
            </button>
            <div className="tm-galeria-video-modal-thumb" style={{ backgroundImage: `url(${videoModal.thumbnail})` }} />
            <div className="tm-galeria-video-modal-info">
              <h3>{videoModal.title}</h3>
              <p>{videoModal.subtitle}</p>
              <p className="tm-galeria-video-modal-note">Próximamente: reproductor integrado.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GaleriaPage;
